import { jsPDF } from "jspdf";

export interface TrialBalancePdfRow {
  code: string;
  name: string;
  depth: number; // 0 = syntetyczne, 1+ = analityczne
  boWn: number;
  boMa: number;
  obrWn: number;
  obrMa: number;
  cumWn: number;
  cumMa: number;
  saldoWn: number;
  saldoMa: number;
  isTotal?: boolean;
}

export interface TrialBalancePdfInput {
  rows: TrialBalancePdfRow[];
  from: string;
  to: string;
  year: number;
  scopeLabel: string;
}

// ── Font loader (cache między wywołaniami) ────────────────────────────────────
let fontRegularB64: string | null = null;
let fontBoldB64: string | null = null;

async function loadFontB64(url: string): Promise<string> {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk)
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(binary);
}

// ── Formatowanie liczb ────────────────────────────────────────────────────────
const fmt = (v: number) =>
  v !== 0
    ? v.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "";

const plDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "—";

// ── Definicja kolumn tabeli (landscape A4: 297mm, marginesy 10mm) ─────────────
// Szerokości w mm; łącznie = 277mm = 297 − 2×10
interface ColDef {
  key: keyof Omit<TrialBalancePdfRow, "depth" | "isTotal">;
  header1: string; // pierwsza linia nagłówka
  header2: string; // druga linia nagłówka
  w: number;
  align: "left" | "right";
}

const COLS: ColDef[] = [
  { key: "code",    header1: "Symbol",       header2: "konta",        w: 22, align: "left"  },
  { key: "name",    header1: "Nazwa konta",  header2: "",             w: 62, align: "left"  },
  { key: "boWn",    header1: "BO",           header2: "Wn",           w: 19, align: "right" },
  { key: "boMa",    header1: "BO",           header2: "Ma",           w: 19, align: "right" },
  { key: "obrWn",   header1: "Obroty",       header2: "Wn",           w: 22, align: "right" },
  { key: "obrMa",   header1: "Obroty",       header2: "Ma",           w: 22, align: "right" },
  { key: "cumWn",   header1: "Obroty Wn",    header2: "narast.",      w: 25, align: "right" },
  { key: "cumMa",   header1: "Obroty Ma",    header2: "narast.",      w: 25, align: "right" },
  { key: "saldoWn", header1: "Saldo",        header2: "Wn",           w: 21, align: "right" },
  { key: "saldoMa", header1: "Saldo",        header2: "Ma",           w: 20, align: "right" },
];

export async function generateTrialBalancePdf(input: TrialBalancePdfInput): Promise<void> {
  if (!fontRegularB64)
    fontRegularB64 = await loadFontB64("/fonts/LiberationSans-Regular.ttf");
  if (!fontBoldB64)
    fontBoldB64 = await loadFontB64("/fonts/LiberationSans-Bold.ttf");

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  doc.addFileToVFS("LiberationSans-Regular.ttf", fontRegularB64);
  doc.addFont("LiberationSans-Regular.ttf", "LiberationSans", "normal");
  doc.addFileToVFS("LiberationSans-Bold.ttf", fontBoldB64);
  doc.addFont("LiberationSans-Bold.ttf", "LiberationSans", "bold");

  const ML = 10; // margin left
  const MT = 10; // margin top
  const PW = 277; // printable width (297 - 2×10)
  const PH = 190; // printable height (210 - 2×10)

  const PURPLE: [number, number, number] = [93, 73, 199];
  const DARK: [number, number, number] = [28, 27, 31];
  const GRAY: [number, number, number] = [120, 113, 130];
  const BG_HEADER: [number, number, number] = [245, 242, 255];
  const BG_SYNTH: [number, number, number] = [250, 248, 255];
  const BG_TOTAL: [number, number, number] = [235, 229, 255];

  const ROW_H = 6.5;   // wysokość wiersza danych
  const HDR_H = 10;    // wysokość nagłówka tabeli
  let y = MT;

  const setF = (size: number, bold: boolean, color: [number,number,number] = DARK) => {
    doc.setFont("LiberationSans", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };

  // ── Nagłówek dokumentu ────────────────────────────────────────────────────
  setF(12, true, PURPLE);
  doc.text("ZESTAWIENIE OBROTÓW I SALD", ML, y + 5);

  setF(8, false, GRAY);
  const subtitle = `Rok: ${input.year}   |   Okres: ${plDate(input.from)} – ${plDate(input.to)}   |   Zakres: ${input.scopeLabel}`;
  doc.text(subtitle, ML, y + 10);

  setF(7, false, GRAY);
  const now = new Date().toLocaleString("pl-PL", {
    day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  doc.text(`Wygenerowano: ${now}`, PW + ML - doc.getTextWidth(`Wygenerowano: ${now}`), y + 5);

  // linia pod nagłówkiem
  doc.setDrawColor(...PURPLE);
  doc.setLineWidth(0.5);
  doc.line(ML, y + 13, ML + PW, y + 13);
  y += 16;

  // ── Funkcja rysowania nagłówka tabeli ─────────────────────────────────────
  const drawTableHeader = (startY: number): number => {
    doc.setFillColor(...BG_HEADER);
    doc.rect(ML, startY, PW, HDR_H, "F");

    doc.setDrawColor(200, 193, 214);
    doc.setLineWidth(0.2);

    let x = ML;
    COLS.forEach((col) => {
      // linia pionowa
      doc.line(x, startY, x, startY + HDR_H);

      setF(7, true, PURPLE);
      const cx = col.align === "right" ? x + col.w - 1.5 : x + 2;
      doc.text(col.header1, cx, startY + 3.5, { align: col.align });
      doc.text(col.header2, cx, startY + 7.5, { align: col.align });

      x += col.w;
    });
    // ostatnia linia pionowa i dolna
    doc.line(x, startY, x, startY + HDR_H);
    doc.setLineWidth(0.4);
    doc.setDrawColor(...PURPLE);
    doc.line(ML, startY + HDR_H, ML + PW, startY + HDR_H);

    return startY + HDR_H;
  };

  // ── Rysowanie wiersza ─────────────────────────────────────────────────────
  const drawRow = (row: TrialBalancePdfRow, rowY: number): void => {
    const isSynth = row.depth === 0 && !row.isTotal;
    const isTotal = !!row.isTotal;

    // tło
    if (isTotal) {
      doc.setFillColor(...BG_TOTAL);
      doc.rect(ML, rowY, PW, ROW_H, "F");
    } else if (isSynth) {
      doc.setFillColor(...BG_SYNTH);
      doc.rect(ML, rowY, PW, ROW_H, "F");
    }

    // linie poziome
    doc.setDrawColor(220, 215, 230);
    doc.setLineWidth(0.15);
    doc.line(ML, rowY + ROW_H, ML + PW, rowY + ROW_H);

    let x = ML;
    COLS.forEach((col) => {
      // linia pionowa
      doc.setDrawColor(220, 215, 230);
      doc.setLineWidth(0.15);
      doc.line(x, rowY, x, rowY + ROW_H);

      const rawVal = row[col.key];
      let text = "";
      if (col.key === "code") {
        text = String(rawVal ?? "");
      } else if (col.key === "name") {
        const indent = row.depth > 0 ? "    " : "";
        const fullName = indent + String(rawVal ?? "");
        // obciąć jeśli za długie
        text = doc.splitTextToSize(fullName, col.w - 3)[0] ?? "";
      } else {
        text = fmt(rawVal as number);
      }

      const bold = isSynth || isTotal;
      setF(7.5, bold, isTotal ? PURPLE : DARK);

      const tx = col.align === "right" ? x + col.w - 1.5 : x + (row.depth > 0 && col.key === "name" ? 4 : 2);
      doc.text(text, tx, rowY + 4.3, { align: col.align });

      x += col.w;
    });
    // ostatnia linia pionowa
    doc.setDrawColor(220, 215, 230);
    doc.line(x, rowY, x, rowY + ROW_H);
  };

  // ── Rysowanie tabeli ──────────────────────────────────────────────────────
  // Oddzielamy wiersz RAZEM od reszty
  const dataRows = input.rows.filter((r) => !r.isTotal);
  const totalRow = input.rows.find((r) => r.isTotal);

  y = drawTableHeader(y);

  const pageBottom = MT + PH - ROW_H; // zostaw miejsce na jeden wiersz

  for (const row of dataRows) {
    if (y + ROW_H > pageBottom) {
      doc.addPage("a4", "landscape");
      y = MT;
      y = drawTableHeader(y);
    }
    drawRow(row, y);
    y += ROW_H;
  }

  // Wiersz RAZEM — zawsze na końcu (na tej samej lub nowej stronie)
  if (totalRow) {
    if (y + ROW_H > pageBottom) {
      doc.addPage("a4", "landscape");
      y = MT;
      y = drawTableHeader(y);
    }
    // pogrubiona linia separatora
    doc.setDrawColor(...PURPLE);
    doc.setLineWidth(0.5);
    doc.line(ML, y, ML + PW, y);
    drawRow(totalRow, y);
  }

  // ── Zapis ─────────────────────────────────────────────────────────────────
  doc.save(`zestawienie-obrotow-i-sald-${input.year}.pdf`);
}
